import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, LayoutGrid } from 'lucide-react'
import { useState, lazy, Suspense } from 'react'

import { GanttChart } from '@/components/pm/gantt'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useProject } from '@/hooks'

// 懒加载任务详情组件
const IssueDetail = lazy(() =>
    import('@/components/pm/issue/issue-detail').then(module => ({ default: module.IssueDetail }))
)

/**
 * 任务详情加载占位符
 */
function IssueDetailSkeleton() {
    return (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl border-l bg-background shadow-lg">
            <div className="border-b px-6 py-4">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <div className="flex gap-2">
                    <Skeleton className="h-4 w-12" />
                    <Skeleton className="h-4 w-12" />
                </div>
            </div>
            <div className="p-6 space-y-6">
                <Skeleton className="h-24 w-full" />
                <div className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                </div>
            </div>
        </div>
    )
}

/**
 * 甘特图页面
 */
export const Route = createFileRoute('/_authenticated/pm/projects/$projectId/gantt')({
    component: GanttChartPage,
})

function GanttChartPage() {
    const params = Route.useParams()
    const projectId = params.projectId
    const navigate = useNavigate()

    const { data: project } = useProject(projectId)

    // 任务详情抽屉状态
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)

    const handleTaskClick = (taskId: string) => {
        setSelectedTaskId(taskId)
    }

    const handleMilestoneClick = (_milestoneId: string) => {
        void navigate({
            to: '/pm/projects/$projectId/milestones',
            params: { projectId },
        })
    }

    return (
        <div className="flex h-full flex-col">
            {/* 头部 */}
            <div className="flex items-center gap-4 pb-4">
                <button
                    onClick={() => void navigate({ to: '/pm/projects/$projectId', params: { projectId } })}
                    className="rounded-md p-2 hover:bg-muted"
                >
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="flex-1">
                    <p className="text-sm text-muted-foreground">{project?.code}</p>
                    <h1 className="text-2xl font-bold tracking-tight">甘特图</h1>
                </div>
                <Link
                    to="/pm/projects/$projectId/board"
                    params={{ projectId }}
                >
                    <Button variant="outline" size="sm">
                        <LayoutGrid className="mr-2 h-4 w-4" />
                        切换到看板
                    </Button>
                </Link>
            </div>

            {/* 甘特图 */}
            <div className="flex-1 overflow-hidden">
                <GanttChart
                    projectId={projectId}
                    onTaskClick={handleTaskClick}
                    onMilestoneClick={handleMilestoneClick}
                />
            </div>

            {selectedTaskId && (
                <Suspense fallback={<IssueDetailSkeleton />}>
                    <IssueDetail
                        issueId={selectedTaskId}
                        onClose={() => setSelectedTaskId(null)}
                    />
                </Suspense>
            )}
        </div>
    )
}
